import React from 'react';
import { useTranslation } from '../../utils/languageUtils';
import './SetPreviewExportPanel.css';

const SetPreviewExportPanel = ({
  exportedImages,
  showExportedImages,
  setShowExportedImages,
  allViewsImages = [],
  configData,
  onAddToCart,
  isTestMode = true,
  isExporting = false,
  statusMessage = ''
}) => {
  const { t } = useTranslation();

  const colorDef = configData?.SupportedModels?.Options?.Option1;
  const colorValues = Array.isArray(colorDef?.values) ? colorDef.values : [];
  const colorMetaByValue = React.useMemo(() => {
    const map = new Map();
    colorValues.forEach((v) => {
      if (!v || v.value == null) return;
      map.set(String(v.value).toLowerCase(), v);
    });
    return map;
  }, [colorValues]);

  const getColorHex = (value) => {
    const meta = colorMetaByValue.get(String(value).toLowerCase());
    const raw = meta?.swatchHex || meta?.swatchhex || meta?.hex || meta?.color;
    if (!raw) return null;
    const s = String(raw).trim();
    if (!s) return null;
    return s.startsWith('#') ? s : `#${s}`;
  };

  const getColorLabel = (value) => {
    const meta = colorMetaByValue.get(String(value).toLowerCase());
    const dn = meta?.displayName;
    if (!dn) return String(value);
    if (typeof dn === 'string') return dn;
    return dn?.en || dn?.cn || String(value);
  };

  if (!showExportedImages) return null;

  const item = Array.isArray(allViewsImages) && allViewsImages.length > 0
    ? allViewsImages[0]
    : exportedImages;

  const preview = item?.preview || null;
  const penMeta = item?.meta?.pen || null;
  const counts = penMeta?.counts && typeof penMeta.counts === 'object' ? penMeta.counts : null;

  return (
    <>
      <div className="spp-backdrop" role="presentation" aria-hidden="true" />

      <div className="spp-panel" onClick={(e) => e.stopPropagation()}>
        <div className="spp-header">
          <div className="spp-title">{t('export.title', 'Export')}</div>
          <button className="spp-close" onClick={() => setShowExportedImages(false)} aria-label={t('export.close', 'Close')}>
            ×
          </button>
        </div>

        {isExporting && (
          <div className="spp-status">
            {statusMessage || t('status.exporting', 'Exporting...')}
          </div>
        )}

        {!isExporting && statusMessage ? (
          <div className="spp-status">{statusMessage}</div>
        ) : null}

        <div className="spp-content">
          <div className="spp-card">
            <div className="spp-card-title">{t('export.preview', 'Preview')}</div>
            {preview ? (
              <img className="spp-img" src={preview} alt="Preview" />
            ) : (
              <div className="spp-placeholder" />
            )}
          </div>

          {counts ? (
            <div className="spp-legend">
              <div className="spp-legend-title">{t('export.selectedColors', 'Selected Colors')}</div>
              <div className="spp-legend-items">
                {Object.entries(counts)
                  .filter(([, q]) => Number(q) > 0)
                  .map(([color, q]) => (
                    <div key={color} className="spp-legend-item">
                      {getColorHex(color) ? (
                        <span
                          className="spp-legend-swatch"
                          style={{ background: getColorHex(color) }}
                          aria-hidden="true"
                        />
                      ) : null}
                      <span className="spp-legend-color">{getColorLabel(color)}</span>
                      <span className="spp-legend-qty">× {Number(q)}</span>
                    </div>
                  ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="spp-footer">
          {typeof onAddToCart === 'function' ? (
            <button className="spp-primary" onClick={onAddToCart} disabled={isExporting}>
              {t('export.addToCart', 'Add to Cart')}
            </button>
          ) : null}

          {isTestMode && preview ? (
            <a className="spp-secondary" href={preview} download>
              {t('export.downloadPreview', 'Download Preview')}
            </a>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default SetPreviewExportPanel;
